import axios from 'axios';

// Vercel production builds must inject REACT_APP_API_URL (e.g. https://api.tenlitor.com).
// All Flask routes start with /api/… so we ensure the base always ends with /api.
const configuredApiUrl = (process.env.REACT_APP_API_URL || '').trim().replace(/\/+$/, '');
const API_BASE_URL = configuredApiUrl
  ? (configuredApiUrl.endsWith('/api') ? configuredApiUrl : configuredApiUrl + '/api')
  : (process.env.NODE_ENV === 'development' ? '/api' : '');
const IS_DEV = process.env.NODE_ENV !== 'production';
const DEBUG_HTTP = String(process.env.REACT_APP_DEBUG_HTTP || '').toLowerCase() === 'true';

if (!API_BASE_URL) {
  throw new Error('REACT_APP_API_URL must be set for production builds.');
}

if (IS_DEV && DEBUG_HTTP) {
  console.log('API_BASE_URL:', API_BASE_URL, 'REACT_APP_API_URL:', process.env.REACT_APP_API_URL);
}

const api = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  timeout: Number(process.env.REACT_APP_API_TIMEOUT_MS || 15000)
});

const normalizeRequestPath = (maybeUrl) => {
  try {
    const s = (maybeUrl || '').toString();
    if (!s) return '';

    // If axios was given an absolute URL, extract the pathname.
    if (/^https?:\/\//i.test(s)) {
      return new URL(s).pathname || '';
    }

    // Axios often uses relative paths like "admin/users".
    return s.startsWith('/') ? s : `/${s}`;
  } catch {
    return '';
  }
};

// Add request instrumentation and keep multipart uploads working.
api.interceptors.request.use((config) => {
  // Debug: log the full URL being requested
  if (IS_DEV && DEBUG_HTTP) {
    console.log('API Request:', config.method?.toUpperCase(), config.baseURL + config.url);
  }

  // When sending FormData, let the browser set Content-Type (with the boundary).
  // The instance-level default 'application/json' would break multipart uploads otherwise.
  if (config.data instanceof FormData) {
    if (config.headers) {
      delete config.headers['Content-Type'];
      delete config.headers['content-type'];
      if (typeof config.headers.delete === 'function') {
        config.headers.delete('Content-Type');
        config.headers.delete('content-type');
      }
      if (typeof config.headers.set === 'function') {
        config.headers.set('Content-Type', undefined);
      }
    }
  }

  return config;
}, (error) => {
  return Promise.reject(error);
});

// Handle responses and errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const requestPath = normalizeRequestPath(error.config?.url);
      const isLoginRequest = requestPath.includes('/login');
      const isPortalRequest = requestPath.startsWith('/admin/') || requestPath.startsWith('/business/');

      const adminToken = localStorage.getItem('admin_token');
      const businessToken = localStorage.getItem('business_token');
      const pendingToken = localStorage.getItem('pending_token');

      // Don't fight the user while they're already logging in.
      // Only hard-redirect when an authenticated portal session is active.
      const isPendingPortal = requestPath.startsWith('/pending/');
      if (!isLoginRequest && (isPortalRequest || isPendingPortal)) {

        // Only clear tokens for the role that received the 401 — avoid cross-role destruction.
        if (requestPath.startsWith('/admin/') && adminToken) {
          localStorage.removeItem('admin_token');
          localStorage.removeItem('admin');
        } else if (requestPath.startsWith('/business/') && businessToken) {
          localStorage.removeItem('business_token');
          localStorage.removeItem('business');
        } else if (requestPath.startsWith('/pending/') && pendingToken) {
          localStorage.removeItem('pending_token');
          localStorage.removeItem('pending_request');
        }
        // Legacy generic token
        localStorage.removeItem('token');
        localStorage.removeItem('user');

        // Redirect to login
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

// ---------------------------------------------------------------------------
// AbortController helper – call from useEffect cleanup to cancel in-flight
// requests when a component unmounts or deps change.
// Usage:
//   const ctl = makeAbortController();
//   api.get('/foo', { signal: ctl.signal }).then(…);
//   return () => ctl.abort();          // in useEffect cleanup
// ---------------------------------------------------------------------------
export const makeAbortController = () => new AbortController();

// ---------------------------------------------------------------------------
// Lightweight GET cache – avoids repeated identical fetches within a TTL.
// ---------------------------------------------------------------------------
const _cache = new Map();
const DEFAULT_TTL_MS = 30_000; // 30 seconds

export const cachedGet = (url, params = {}, ttlMs = DEFAULT_TTL_MS) => {
  const key = url + '?' + JSON.stringify(params);
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.ts < ttlMs) {
    return Promise.resolve(hit.data);
  }
  return api.get(url, { params }).then(r => {
    _cache.set(key, { data: r.data, ts: Date.now() });
    return r.data;
  });
};

export const invalidateCache = (urlPrefix) => {
  for (const key of _cache.keys()) {
    if (key.startsWith(urlPrefix)) _cache.delete(key);
  }
};

// Public-facing API helpers
export const getPublicBusinesses = (params = {}) => {
  return api.get('/public/businesses', { params }).then(r => r.data);
};

export const getPublicBusiness = (businessKey) => {
  const normalizedKey = String(businessKey ?? '').trim();
  if (!normalizedKey) {
    return Promise.reject(new Error('Missing business key'));
  }

  if (/^\d+$/.test(normalizedKey)) {
    return api.get(`/public/businesses/${normalizedKey}`).then(r => r.data);
  }

  return api.get(`/public/businesses/resolve/${encodeURIComponent(normalizedKey)}`).then(r => r.data);
};

export const getPublicBusinessSlots = (businessId, params = {}) => {
  return api.get(`/public/businesses/${businessId}/slots`, { params }).then(r => r.data);
};

export const getPublicCities = () => {
  return api.get('/public/cities').then(r => r.data);
};

export const getPublicLocationSuggestions = (params = {}) => {
  return api.get('/public/geocode/suggest', { params }).then(r => r.data);
};

export const geocodePublicLocation = (params = {}) => {
  return api.get('/public/geocode', { params }).then(r => r.data);
};

export const reverseGeocodePublicLocation = (params = {}) => {
  return api.get('/public/geocode/reverse', { params }).then(r => r.data);
};

export const getPublicBusinessSuggestions = (params = {}) => {
  return api.get('/public/businesses/suggest', { params }).then(r => r.data);
};
